import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { type WorkspaceCaptions, type WorkspaceTitles } from '@backend/lib/db/schema/workspace'
import { WorkspaceHeaderSocialAccounts } from './workspace-header-social-accounts'
import { WorkspaceStatusBadge } from './workspace-status-badge'
import { type UpdateScheduledContentFn } from '@/lib/workspace-post-status'
import { WorkspaceExportDialog } from '@/components/workspace/workspace-export-dialog'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'
import {
  InfoIcon,
  MagicIcon,
  Post1Icon,
  ReelsIcon,
  SettingsIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
  FacebookIcon,
  MetaAdsIcon,
} from '@/components/ui/icons/icons-new'
import { useChatStore } from '@/stores/chatStore'
import {
  analyzePostType,
  checkCompatibility,
  getPostTypeBadge,
  type CaptionLengths,
} from '@/lib/workspace-media-validation'
import { platformTypes } from '@backend/lib/db/schema/platform'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/shadcn/hover-card'
import { PostButton } from '@/components/ui/buttons/post-button'
import { ChatButton } from '@/components/ui/buttons/chat-button'
import { WarningInfo } from '@/components/ui/warning-info'
import { Button } from '@/components/ui/shadcn/button'
import { ImageIcon } from 'lucide-react'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { type CaptionRating } from '@backend/lib/ai/tools/captionRating'

/** Per-platform icon + color for warning badges. Adding a platform = adding one entry. */
const WARNING_PLATFORM_ICON: Record<PlatformType, { icon: React.ReactNode; bgColor: string }> = {
  instagram: {
    icon: <InstagramIcon width={12} height={12} color="white" />,
    bgColor: 'bg-pink-500',
  },
  linkedin: {
    icon: <LinkedinIcon width={12} height={12} color="white" />,
    bgColor: 'bg-blue-500',
  },
  tiktok: {
    icon: <TikTokIcon width={12} height={12} color="white" />,
    bgColor: 'bg-black',
  },
  youtube: {
    icon: <YoutubeIcon width={12} height={12} color="white" />,
    bgColor: 'bg-red-600',
  },
  facebook: {
    icon: <FacebookIcon width={12} height={12} color="white" />,
    bgColor: 'bg-[#1877F2]',
  },
  'meta-ads': {
    icon: <MetaAdsIcon width={12} height={12} color="white" />,
    bgColor: 'bg-[#0866FF]',
  },
}

interface Props {
  /** Whether this post publishes with a visible AI label baked in, so the export
   *  can include it. */
  bakeAiLabel?: boolean
  aiLabelStyle?: AiLabelStyle
  aiProvenance?: 'generated' | 'composite' | null
  workspaceId: number
  onUpdateScheduled?: UpdateScheduledContentFn
  isSaving?: boolean
  media: WorkspaceMedia[]
  captions: WorkspaceCaptions
  platformTitles?: WorkspaceTitles
  workspaceTitle: string
  selectedCaptionPlatform: PlatformType
  onCaptionPlatformChange: (platform: PlatformType) => void
  captionLength: CaptionLengths
  onPreviewClick: () => void
  activePlatforms: PlatformType[]
  onActivePlatformsChange: (platforms: PlatformType[]) => void
  onSettingsClick?: () => void
  onThumbnailClick?: () => void
  hasThumbnail?: boolean
  rating?: CaptionRating | null
  isRatingLoading?: boolean
  onAnalyzeRating?: () => void
}

function PostRatingRing({
  rating,
  isLoading,
  onAnalyze,
}: {
  rating?: CaptionRating | null
  isLoading?: boolean
  onAnalyze?: () => void
}) {
  const size = 36
  const strokeWidth = 3
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  if (isLoading) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="hover:bg-hover rounded-lg border-none">
              <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="animate-spin">
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="var(--border)"
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="var(--foreground)"
                  strokeWidth={strokeWidth}
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * 0.75}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
              </svg>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Analyzing post...</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (!rating) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="hover:bg-hover rounded-lg border-none"
              onClick={onAnalyze}>
              <MagicIcon width={16} height={16} color="var(--foreground)" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Analyze post rating</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  const avg =
    (rating.authenticity + rating.connection + rating.engagement + rating.impact + rating.reach) / 5
  const percentage = (avg / 10) * 100
  const dashoffset = circumference * (1 - percentage / 100)

  const ringColor = avg >= 7 ? 'var(--success)' : avg >= 4 ? 'var(--warning)' : 'var(--destructive)'

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="hover:bg-hover relative rounded-lg border-none"
          onClick={onAnalyze}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="var(--border)"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={dashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              className="transition-all duration-500"
            />
            <text
              x={size / 2}
              y={size / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fill="var(--foreground)"
              fontSize="11"
              fontWeight="600">
              {avg.toFixed(1)}
            </text>
          </svg>
        </Button>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="end" className="w-72 p-0">
        <PostRatingHoverContent rating={rating} onAnalyze={onAnalyze} />
      </HoverCardContent>
    </HoverCard>
  )
}

function PostRatingHoverContent({
  rating,
  onAnalyze,
}: {
  rating: CaptionRating
  onAnalyze?: () => void
}) {
  const metrics = [
    { key: 'authenticity' as const, label: 'Authenticity' },
    { key: 'connection' as const, label: 'Connection' },
    { key: 'engagement' as const, label: 'Engagement' },
    { key: 'impact' as const, label: 'Impact' },
    { key: 'reach' as const, label: 'Reach' },
  ]

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-foreground text-sm font-medium">Post Rating</span>
        {onAnalyze && (
          <Button variant="ghost" size="sm" onClick={onAnalyze} className="h-7 text-xs">
            <MagicIcon color="var(--foreground)" />
            <span>Re-analyze</span>
          </Button>
        )}
      </div>
      {rating.encouragementText && (
        <p className="text-muted-foreground mb-3 text-xs leading-relaxed">
          {rating.encouragementText}
        </p>
      )}
      <div className="space-y-2.5">
        {metrics.map(({ key, label }) => {
          const value = rating[key]
          const pct = (value / 10) * 100
          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="text-foreground font-medium">{value}/10</span>
              </div>
              <div className="bg-muted h-1 overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full bg-green-500 transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function WorkspaceHeader({
  bakeAiLabel,
  aiLabelStyle,
  aiProvenance,
  workspaceId,
  onUpdateScheduled,
  isSaving,
  media,
  captions,
  platformTitles,
  workspaceTitle,
  selectedCaptionPlatform,
  onCaptionPlatformChange,
  captionLength,
  onPreviewClick,
  activePlatforms,
  onActivePlatformsChange,
  onSettingsClick,
  onThumbnailClick,
  hasThumbnail,
  rating,
  isRatingLoading,
  onAnalyzeRating,
}: Props) {
  const { toggleChat } = useChatStore()
  const hasCaption = Object.values(captionLength).some((n) => n > 0)
  const postType = analyzePostType(media, hasCaption)
  const compatibility = checkCompatibility(postType, captionLength)

  const warnings: { platform: PlatformType; message: string }[] = []
  for (const platform of platformTypes) {
    if (activePlatforms.includes(platform) && !compatibility[platform].compatible) {
      warnings.push({ platform, message: compatibility[platform].reason! })
    }
  }

  const hasWarning = warnings.length > 0

  const postTypeBadge = getPostTypeBadge(postType)

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Wraps on phones: in one row the Post button was pushed off-screen. */}
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <WorkspaceHeaderSocialAccounts
          selectedPlatform={selectedCaptionPlatform}
          onSelectedPlatformChange={onCaptionPlatformChange}
          activePlatforms={activePlatforms}
          onActivePlatformsChange={onActivePlatformsChange}
        />

        <div className="ml-auto flex items-center gap-2">
          <ChatButton onClick={toggleChat} />

          <WorkspaceExportDialog
            workspaceMedia={media}
            captions={captions}
            platformTitles={platformTitles}
            workspaceTitle={workspaceTitle}
            activePlatforms={activePlatforms}
            bakeAiLabel={bakeAiLabel}
            aiLabelStyle={aiLabelStyle}
            aiProvenance={aiProvenance}
          />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative">
                  <PostButton onClick={onPreviewClick} />
                  <WarningInfo hasWarning={hasWarning} />
                </div>
              </TooltipTrigger>
              {hasWarning && (
                <TooltipContent className="max-w-sm">
                  <div className="space-y-0">
                    {warnings.map((warning, index) => {
                      const platformInfo = WARNING_PLATFORM_ICON[warning.platform]

                      return (
                        <div key={index} className="flex items-center gap-3">
                          <div className="shrink-0">
                            <div className="flex items-center gap-2">
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-400">
                                <InfoIcon width={12} height={12} color="black" />
                              </div>
                              <div
                                className={`flex h-5 w-5 items-center justify-center rounded-full ${platformInfo.bgColor}`}>
                                {platformInfo.icon}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm">{warning.message}</p>
                        </div>
                      )
                    })}
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          {postType.type !== 'empty' && (
            <div className="border-border bg-card rounded-full border px-5 py-0.5">
              <span className="font-regular flex items-center gap-2 text-sm">
                {postType.type === 'single-video' ? (
                  <ReelsIcon width={16} height={16} color="var(--foreground)" />
                ) : (
                  <Post1Icon width={16} height={16} color="var(--foreground)" />
                )}

                {postTypeBadge}
              </span>
            </div>
          )}
          <WorkspaceStatusBadge
            workspaceId={workspaceId}
            platform={selectedCaptionPlatform}
            onUpdateScheduled={onUpdateScheduled}
            isSaving={isSaving}
          />
        </div>
        <div className="flex items-center gap-1">
          <PostRatingRing rating={rating} isLoading={isRatingLoading} onAnalyze={onAnalyzeRating} />
          {onThumbnailClick && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="hover:bg-hover relative rounded-lg border-none disabled:opacity-30"
                    disabled={!media.some((m) => m.type === 'video')}
                    onClick={onThumbnailClick}>
                    <ImageIcon className="h-[22px] w-[22px]" />
                    {hasThumbnail && (
                      <span className="bg-primary absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm" side="bottom">
                  <p>Video thumbnail</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="hover:bg-hover rounded-lg border-none"
                  onClick={onSettingsClick}>
                  <SettingsIcon width={22} height={22} color="var(--foreground)" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm" side="bottom">
                <p>Settings</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  )
}
